import { useQuery } from '@tanstack/react-query';
import { IdCheckResult } from '@cloudsign/shared';
import { ApiClientError, api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { errorText, t } from '../../lib/i18n';
import { useCan, useCaps } from '../../lib/session';
import { Alert, Badge } from '../../components/ui';

const TONE = { pass: 'good', warn: 'warn', refuse: 'bad', skipped: 'neutral' } as const;

const yesNo = (v: boolean | null) => (v === null ? t('idResult.unknown') : v ? t('common.yes') : t('common.no'));

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-3 text-sm">
      <dt className="text-slate-600">{label}</dt>
      <dd className="col-span-2 text-slate-900">{children}</dd>
    </div>
  );
}

/**
 * ID check result of one visit (id-checks module, GET /id-checks/visits/:visitId, needs id_checks.view_result). Yes/no answers only:
 * the server never keeps document text. A 404 means no check was recorded for this visit.
 */
export function IdCheckResultSection({ visitId, timeZone }: { visitId: string; timeZone: string }) {
  const caps = useCaps();
  const allowed = useCan('id_checks.view_result') && caps.modules.some((m) => m.key === 'id-checks' && m.enabled);
  const q = useQuery({
    queryKey: ['today', 'id-check', visitId],
    enabled: allowed,
    retry: false,
    queryFn: () => api.get(`/id-checks/visits/${visitId}`, { schema: IdCheckResult }),
  });
  if (!allowed || q.isPending) return null;
  const missing = q.isError && q.error instanceof ApiClientError && q.error.status === 404;
  return (
    <section aria-labelledby="drawer-idcheck" className="space-y-2">
      <h3 id="drawer-idcheck" className="text-sm font-semibold text-slate-900">{t('idResult.title')}</h3>
      {missing ? <p className="text-sm text-slate-600">{t('idResult.none')}</p> : null}
      {q.isError && !missing ? <Alert>{q.error instanceof ApiClientError ? errorText(q.error.body) : t('error.internal')}</Alert> : null}
      {q.data ? (
        <dl className="space-y-2">
          <Row label={t('idResult.title')}>
            <Badge tone={TONE[q.data.outcome]}>{t(`idResult.outcome.${q.data.outcome}`)}</Badge>
          </Row>
          {q.data.documentType ? <Row label={t('idResult.document')}>{t(`setup.idc.doc.${q.data.documentType}`)}</Row> : null}
          {q.data.method ? <Row label={t('idResult.method')}>{t(`idResult.method.${q.data.method}`)}</Row> : null}
          {q.data.outcome !== 'skipped' ? <Row label={t('idResult.expiry')}>{yesNo(q.data.expiryOk)}</Row> : null}
          {q.data.outcome !== 'skipped' ? <Row label={t('idResult.name')}>{yesNo(q.data.nameMatch)}</Row> : null}
          {q.data.skipReason ? <Row label={t('idResult.skipReason')}>{q.data.skipReason}</Row> : null}
          <Row label={t('idResult.checkedAt')}>{formatDateTime(q.data.checkedAt, timeZone)}</Row>
          {q.data.hasImage ? (
            <p className="text-sm text-slate-700">
              {q.data.imageDeleteAfter ? t('idResult.image', { date: formatDateTime(q.data.imageDeleteAfter, timeZone) }) : t('idResult.imageNoDate')}
            </p>
          ) : null}
        </dl>
      ) : null}
    </section>
  );
}
